/* ============================================================================
 *  Adding to a stay — rounds, observations and nursing notes
 * ----------------------------------------------------------------------------
 *  WRITES THROUGH THE WARD'S OWN ENDPOINTS, NOT NEW ONES
 *
 *  Every form here posts to the endpoint the ward screens already use:
 *
 *      /saveAdmissionInfoRound   the doctor's ward round
 *      /saveVitals               the nurse's observations
 *      /saveNurseNote            the shift note
 *
 *  That is the whole design. A round added from the claims screen has to BE a
 *  round — the same row, in the same table, that the consulting room and the
 *  ward read. A second write path would have produced a second version of the
 *  record that only the claims screen could see, which is worse than not being
 *  able to add one at all.
 *
 *  ── HOW A SECOND ROUND ON ONE DAY IS POSSIBLE ──────────────────────────────
 *
 *  /saveAdmissionInfoRound upserts on (date, doctor, state, patient). So the
 *  ROUND STATE is what separates two rounds on the same day — Morning,
 *  Afternoon, Evening, Review. Choosing a state that day already has edits that
 *  round; choosing a new one adds a round. That is why the state is a required
 *  choice on the form and not a hidden default: it is the key.
 *
 *  Nursing notes are different — they are appended, several a shift, with no
 *  key to collide on, so `mode: 'append'` inserts every time. Observations are
 *  keyed by their own trans_code, so a new one is simply a new reading.
 *
 *  ── WHOSE NAME GOES ON IT ──────────────────────────────────────────────────
 *
 *  The signed-in user's. Not a free-text box, and not blank: a clinical entry
 *  has to carry who made it, and letting a claims officer type any doctor's
 *  name onto a ward round is the one thing this must not allow.
 * ========================================================================== */

import React, { useState } from 'react';
import axios from 'axios';
import { Loader2, Check, X } from 'lucide-react';
import { saveAdmissionInfoRound, saveVitalsdb, saveNurseNotedb } from '../BaseURL';
import { BRAND } from '../../brand';

//  The rounds a day can hold. 'Admission' is deliberately absent: it is the
//  first-day assessment written when the patient was admitted, not something to
//  add days later from a claims screen.
export const ROUND_STATES = ['Morning Round', 'Afternoon Round', 'Evening Round',
  'Review Round'];

const now = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const input = {
  width: '100%', padding: '6px 8px', borderRadius: 6, fontSize: 12,
  border: `1px solid ${BRAND.line}`, font: 'inherit', color: BRAND.ink,
  background: '#fff', boxSizing: 'border-box',
};
const label = {
  display: 'block', fontSize: 10, fontWeight: 800, color: BRAND.inkMuted,
  textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 3,
};

function Shell({ title, busy, error, onCancel, onSave, saveLabel, children }) {
  return (
    <div style={{
      marginTop: 8, padding: 10, borderRadius: 8,
      background: '#F8FAFC', border: `1px solid ${BRAND.line}`,
    }}>
      <div style={{
        fontSize: 10.5, fontWeight: 800, color: BRAND.navy, marginBottom: 7,
        textTransform: 'uppercase', letterSpacing: '.04em',
      }}>{title}</div>
      <div style={{ display: 'grid', gap: 7 }}>{children}</div>
      {error && (
        <div style={{ marginTop: 7, fontSize: 11.5, color: BRAND.danger }}>{error}</div>
      )}
      <div style={{ display: 'flex', gap: 6, marginTop: 9, justifyContent: 'flex-end' }}>
        <button type="button" onClick={onCancel} disabled={busy}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '5px 9px', borderRadius: 6, fontSize: 11.5, fontWeight: 700,
            border: `1px solid ${BRAND.line}`, background: '#fff',
            color: BRAND.inkMuted, cursor: busy ? 'default' : 'pointer', font: 'inherit',
          }}><X size={12} /> Cancel</button>
        <button type="button" onClick={onSave} disabled={busy}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '5px 11px', borderRadius: 6, fontSize: 11.5, fontWeight: 800,
            border: 0, background: BRAND.green, color: '#fff',
            cursor: busy ? 'default' : 'pointer', font: 'inherit',
          }}>
          {busy ? <Loader2 size={12} style={{ animation: 'tc-spin 1s linear infinite' }} />
            : <Check size={12} />}
          {busy ? 'Saving…' : (saveLabel || 'Save')}
        </button>
      </div>
    </div>
  );
}

/* ── A WARD ROUND ─────────────────────────────────────────────────────────── */
export function RoundForm({ patientId, day, currentUser, existing, onDone, onCancel }) {
  const [state, setState] = useState(existing?.state || ROUND_STATES[0]);
  const [complaint, setComplaint] = useState(existing?.complaint || '');
  const [exam, setExam] = useState(existing?.exam || '');
  const [plan, setPlan] = useState(existing?.plan || '');
  const [other, setOther] = useState(existing?.other || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  //  Editing keeps the ORIGINAL doctor's name, because that is the key the
  //  upsert matches on — changing it would silently create a second round
  //  rather than correct the one on screen. A new round is signed by whoever is
  //  adding it.
  const doctor = existing?.doctors?.[0] || currentUser || '';

  const save = async () => {
    if (!complaint.trim() && !exam.trim() && !plan.trim() && !other.trim()) {
      setError('Write something in at least one box before saving.');
      return;
    }
    setBusy(true); setError(null);
    try {
      const { data } = await axios.post(saveAdmissionInfoRound, {
        patientId,
        //  The endpoint updates only NON-EMPTY fields, so '' means "leave it
        //  alone" rather than "clear it". Sending a single space would clear a
        //  box the officer emptied on purpose — but that is a delete, and this
        //  form does not delete.
        symptoms: complaint,
        ex_result: exam,
        Other_doc: other,
        Surgical_Notes: existing?.surgical || '',
        plan,
        state,
        dat: day,
        time: now(),
        doctor,
      });
      if (data?.success === false) throw new Error(data.message || 'Save refused.');
      onDone();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'The round could not be saved.');
    } finally { setBusy(false); }
  };

  return (
    <Shell title={existing ? `Edit the ${existing.state}` : 'Add a ward round'}
      busy={busy} error={error} onCancel={onCancel} onSave={save}
      saveLabel={existing ? 'Save changes' : 'Add round'}>
      {!existing && (
        <div>
          <span style={label}>Which round</span>
          <select value={state} onChange={(e) => setState(e.target.value)} style={input}>
            {ROUND_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          {/*  The key, said out loud. Without this the officer picks a state
               that already exists, the upsert quietly overwrites that round,
               and the round they meant to add never appears. */}
          <div style={{ fontSize: 10.5, color: BRAND.inkFaint, marginTop: 3 }}>
            A day can hold one of each. Choosing a round this day already has
            will edit that round rather than add another.
          </div>
        </div>
      )}
      <div>
        <span style={label}>Complaint</span>
        <textarea value={complaint} onChange={(e) => setComplaint(e.target.value)}
          rows={2} style={{ ...input, resize: 'vertical' }}
          placeholder="How the patient is today" />
      </div>
      <div>
        <span style={label}>On examination</span>
        <textarea value={exam} onChange={(e) => setExam(e.target.value)}
          rows={3} style={{ ...input, resize: 'vertical' }}
          placeholder="What was found on the round" />
      </div>
      <div>
        <span style={label}>Plan</span>
        <textarea value={plan} onChange={(e) => setPlan(e.target.value)}
          rows={2} style={{ ...input, resize: 'vertical' }}
          placeholder="What happens next" />
      </div>
      <div>
        <span style={label}>Other</span>
        <textarea value={other} onChange={(e) => setOther(e.target.value)}
          rows={2} style={{ ...input, resize: 'vertical' }} />
      </div>
      <div style={{ fontSize: 10.5, color: BRAND.inkFaint }}>
        Filed as <b>{doctor || 'no name'}</b> on {day}. This is the same row the
        ward and consulting room read.
      </div>
    </Shell>
  );
}

/* ── AN OBSERVATION ───────────────────────────────────────────────────────── */
export function VitalsForm({ patientId, patientName, day, currentUser, onDone, onCancel }) {
  const [v, setV] = useState({ tem: '', pulse: '', res: '', pre: '', spo2: '', time: now() });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setV((o) => ({ ...o, [k]: e.target.value }));

  const save = async () => {
    if (!v.tem && !v.pulse && !v.res && !v.pre && !v.spo2) {
      setError('Enter at least one reading.');
      return;
    }
    setBusy(true); setError(null);
    try {
      const { data } = await axios.post(saveVitalsdb, {
        patientId,
        tem: v.tem, pulse: v.pulse, res: v.res, pre: v.pre, spo2: v.spo2,
        we: '', high: '', bmi: '',
        user: currentUser || '', name: patientName || '',
        //  Left blank on purpose. The endpoint parses age to an INT and falls
        //  back to NULL; sending a display string like "4 yrs" is what made it
        //  reject the row before that guard existed.
        age: '', gender: '', status: '',
        dat_vist: day, time: v.time,
      });
      if (data?.success === false) throw new Error(data.message || 'Save refused.');
      onDone();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'The reading could not be saved.');
    } finally { setBusy(false); }
  };

  return (
    <Shell title="Add an observation" busy={busy} error={error}
      onCancel={onCancel} onSave={save} saveLabel="Add reading">
      <div style={{ display: 'grid', gap: 7, gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div><span style={label}>Time</span>
          <input type="time" value={v.time} onChange={set('time')} style={input} /></div>
        <div><span style={label}>Temp °C</span>
          <input value={v.tem} onChange={set('tem')} style={input} placeholder="36.8" /></div>
        <div><span style={label}>Pulse</span>
          <input value={v.pulse} onChange={set('pulse')} style={input} placeholder="82" /></div>
        <div><span style={label}>Resp</span>
          <input value={v.res} onChange={set('res')} style={input} placeholder="20" /></div>
        <div><span style={label}>BP</span>
          <input value={v.pre} onChange={set('pre')} style={input} placeholder="120/80" /></div>
        <div><span style={label}>SpO₂ %</span>
          <input value={v.spo2} onChange={set('spo2')} style={input} placeholder="98" /></div>
      </div>
      <div style={{ fontSize: 10.5, color: BRAND.inkFaint }}>
        Recorded by <b>{currentUser || 'no name'}</b> on {day}. Saved to the same
        chart the ward reads.
      </div>
    </Shell>
  );
}

/* ── A NURSING NOTE ───────────────────────────────────────────────────────── */
export function NoteForm({ patientId, patientName, day, currentUser, onDone, onCancel }) {
  const [note, setNote] = useState('');
  const [time, setTime] = useState(now());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const save = async () => {
    if (!note.trim()) { setError('Write the note before saving.'); return; }
    if (!currentUser) { setError('You must be signed in to sign a nursing note.'); return; }
    setBusy(true); setError(null);
    try {
      const { data } = await axios.post(saveNurseNotedb, {
        patientId, note, user: currentUser, name: patientName || '',
        age: '', gen: '', dat: day, time,
        //  Append, always. Notes are several a shift and have no key to collide
        //  on — the upsert path would overwrite the previous note of the day.
        mode: 'append',
      });
      if (data?.success === false) throw new Error(data.message || 'Save refused.');
      onDone();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'The note could not be saved.');
    } finally { setBusy(false); }
  };

  return (
    <Shell title="Add a nursing note" busy={busy} error={error}
      onCancel={onCancel} onSave={save} saveLabel="Add note">
      <div style={{ maxWidth: 130 }}>
        <span style={label}>Time</span>
        <input type="time" value={time} onChange={(e) => setTime(e.target.value)} style={input} />
      </div>
      <div>
        <span style={label}>Note</span>
        <textarea value={note} onChange={(e) => setNote(e.target.value)}
          rows={4} style={{ ...input, resize: 'vertical' }}
          placeholder="What was observed, what was given, how the patient is" />
      </div>
      <div style={{ fontSize: 10.5, color: BRAND.inkFaint }}>
        Signed <b>{currentUser || 'not signed in'}</b> on {day}. Added alongside the
        day's other notes, not over them.
      </div>
    </Shell>
  );
}
